import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Textarea,
  useToast,
  useColorModeValue,
  IconButton,
  Progress,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Image,
  Center,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer
} from '@chakra-ui/react';
import { FiArrowLeft, FiX, FiPlus } from 'react-icons/fi';
import IconWrapper from '../components/IconWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { GENRES } from '../constants/genres';
import { CONTENT_RATINGS } from '../constants/contentRatings';
import { uploadFileDirect, createContentDocumentDirect } from '../services/uploadService';
import { db } from '../lib/firebase';
import { doc, updateDoc } from 'firebase/firestore';

interface VolumeData {
  id: string;
  title: string;
  coverFile: File | null;
  coverUrl: string;
  pages?: number;
  description: string;
  file?: File | null;
  fileName?: string;
}

const UploadComic: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    author: '',
    genres: [] as string[],
    rating: 'All Ages' as string,
    language: 'English',
    tags: '',
    cast: '',
    director: '',
    writer: '',
    coverFile: null as File | null,
    coverUrl: '',
    volumes: [] as VolumeData[]
  });
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const inputBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.900', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const backButtonBg = useColorModeValue('purple.100', 'purple.800');
  const backButtonColor = useColorModeValue('purple.700', 'white');
  const backButtonHoverBg = useColorModeValue('purple.200', 'purple.700');
  const addCoverButtonBg = useColorModeValue('purple.500', 'purple.600');
  const addCoverButtonHoverBg = useColorModeValue('purple.600', 'purple.500');
  const coverIconColor = useColorModeValue('gray.400', 'gray.300');
  const coverTextColor = useColorModeValue('gray.500', 'gray.400');

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGenreToggle = (genre: string) => {
    const isSelected = formData.genres.includes(genre);
    
    if (isSelected) {
      handleInputChange('genres', formData.genres.filter(g => g !== genre));
    } else if (formData.genres.length < 3) {
      handleInputChange('genres', [...formData.genres, genre]);
    }
  };

  const handleCoverSelect = (file: File) => {
    handleInputChange('coverFile', file);
    const url = URL.createObjectURL(file);
    handleInputChange('coverUrl', url);
  };

  const handleCoverRemove = () => {
    handleInputChange('coverFile', null);
    handleInputChange('coverUrl', '');
  };

  const addVolume = () => {
    const newVolume: VolumeData = {
      id: `volume-${Date.now()}`,
      title: '',
      coverFile: null,
      coverUrl: '',
      pages: undefined,
      description: '',
      file: null,
      fileName: ''
    };
    handleInputChange('volumes', [...formData.volumes, newVolume]);
  };

  const updateVolume = (volumeId: string, field: keyof VolumeData, value: any) => {
    const updatedVolumes = formData.volumes.map(vol => 
      vol.id === volumeId ? { ...vol, [field]: value } : vol
    );
    handleInputChange('volumes', updatedVolumes);
  };

  const updateVolumeFile = (volumeId: string, file: File) => {
    const updatedVolumes = formData.volumes.map(vol => 
      vol.id === volumeId ? { ...vol, file: file, fileName: file.name } : vol
    );
    handleInputChange('volumes', updatedVolumes);
  };

  const removeVolume = (volumeId: string) => {
    const updatedVolumes = formData.volumes.filter(vol => vol.id !== volumeId);
    handleInputChange('volumes', updatedVolumes);
  };

  const handleVolumeCoverSelect = (volumeId: string, file: File) => {
    const url = URL.createObjectURL(file);
    
    // Update both coverFile and coverUrl in a single state update
    const updatedVolumes = formData.volumes.map(vol => 
      vol.id === volumeId ? { ...vol, coverFile: file, coverUrl: url } : vol
    );
    handleInputChange('volumes', updatedVolumes);
  };

  const nextStep = () => {
    if (currentStep === 1) {
      if (!formData.title.trim()) {
        toast({
          title: 'Title required',
          description: 'Please enter a comic title.',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      if (!formData.description.trim()) {
        toast({
          title: 'Description required',
          description: 'Please enter a comic description.',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      if (formData.genres.length === 0) {
        toast({
          title: 'Genre required',
          description: 'Please select at least one genre.',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }
    setCurrentStep(2);
    // Scroll to top when moving to next step
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const prevStep = () => {
    setCurrentStep(1);
    // Scroll to top when going back
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!user) return;

    if (formData.volumes.length === 0) {
      toast({
        title: 'Volumes required',
        description: 'Please add at least one volume to your comic.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Upload comic cover
      let coverUrl = '';
      if (formData.coverFile) {
        coverUrl = await uploadFileDirect(
          `comics/${user.uid}/covers`, 
          formData.coverFile,
          (progress) => {
            setUploadProgress(progress * 0.2); // 0-20% for cover
          }
        );
      }

      // Create comic document
      const comicData = {
        title: formData.title,
        description: formData.description,
        category: 'comic',
        visibility: 'public',
        rating: formData.rating,
        language: formData.language,
        genres: formData.genres,
        tags: formData.tags || '',
        cast: formData.cast.split(',').map(name => name.trim()),
        director: formData.director.split(',').map(name => name.trim()),
        writer: formData.writer.split(',').map(name => name.trim()),
        author: formData.author,
        seasonNumber: '',
        episodeNumber: '',
        chapterNumber: ''
      };

      const assetUrls: { [key: string]: string } = {};
      if (coverUrl) assetUrls.poster = coverUrl;

      const comicId = await createContentDocumentDirect(user.uid, comicData, assetUrls);
      setUploadProgress(40);

      // Upload volumes: cover + content file (PDF or images - start with PDF only)
      const volumesMeta: Array<{ title: string; description: string; volumeNumber: number; pages?: number; coverUrl?: string; fileUrl?: string; }> = [];
      for (let i = 0; i < formData.volumes.length; i++) {
        const volume = formData.volumes[i];

        // Validate file
        if (!volume.file) {
          toast({
            title: `Volume ${i + 1} file required`,
            description: 'Please select a PDF file for this volume.',
            status: 'warning',
            duration: 3000,
            isClosable: true,
          });
          setIsUploading(false);
          return;
        }
        if (volume.file && volume.file.type !== 'application/pdf') {
          toast({
            title: 'Invalid file type',
            description: 'Volume file must be a PDF.',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          setIsUploading(false);
          return;
        }

        let volumeCoverUrl = '';
        if (volume.coverFile) {
          try {
            volumeCoverUrl = await uploadFileDirect(
              `comics/${user.uid}/${comicId}/volumes/${i + 1}/covers`, 
              volume.coverFile
            );
          } catch (error) {
            console.error(`Error uploading cover for volume ${i + 1}:`, error);
          }
        }

        // Upload volume file (PDF)
        let volumeFileUrl = '';
        if (volume.file) {
          const volumeProgressStart = 40 + (i / formData.volumes.length) * 50;
          const volumeProgressRange = 50 / formData.volumes.length;
          volumeFileUrl = await uploadFileDirect(
            `comics/${user.uid}/${comicId}/volumes/${i + 1}`, 
            volume.file,
            (progress) => {
              setUploadProgress(volumeProgressStart + (progress / 100) * volumeProgressRange);
            }
          );
        }

        volumesMeta.push({
          title: volume.title,
          description: volume.description,
          volumeNumber: i + 1,
          pages: volume.pages || undefined,
          coverUrl: volumeCoverUrl || undefined,
          fileUrl: volumeFileUrl || undefined,
        });

        setUploadProgress(40 + ((i + 1) / formData.volumes.length) * 50);
      }

      // Update comic document with volumes metadata under assets.volumes
      await updateDoc(doc(db, 'content', comicId), {
        'assets.volumes': volumesMeta
      });

      setUploadProgress(100);

      toast({
        title: 'Comic uploaded successfully!',
        description: 'Your comic with volumes has been published.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      navigate('/');
    } catch (error) {
      console.error('Error uploading comic:', error);
      toast({
        title: 'Upload failed',
        description: 'There was an error uploading your comic. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const renderStep1 = () => (
    <VStack gap={6} align="stretch">
      {/* Title */}
      <FormControl>
        <FormLabel color={labelColor} fontWeight="bold">
          Comic Title *
        </FormLabel>
        <Input
          value={formData.title}
          onChange={(e) => handleInputChange('title', e.target.value)}
          placeholder="Enter comic title"
          bg={inputBg}
          borderColor={borderColor}
        />
      </FormControl>

      {/* Description */}
      <FormControl>
        <FormLabel color={labelColor} fontWeight="bold">
          Description *
        </FormLabel>
        <Textarea
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          placeholder="Enter comic description"
          bg={inputBg}
          borderColor={borderColor}
          rows={4}
        />
      </FormControl>

      {/* Author */}
      <FormControl>
        <FormLabel color={labelColor} fontWeight="bold">
          Author
        </FormLabel>
        <Input
          value={formData.author}
          onChange={(e) => handleInputChange('author', e.target.value)}
          placeholder="Enter author name"
          bg={inputBg}
          borderColor={borderColor}
        />
      </FormControl>

      {/* Genres */}
      <FormControl>
        <FormLabel color={labelColor} fontWeight="bold">
          Genres * (Select up to 3)
        </FormLabel>
        <Flex wrap="wrap" gap={2}>
          {GENRES.map(genre => (
            <Button
              key={genre}
              size="sm"
              variant={formData.genres.includes(genre) ? 'solid' : 'outline'}
              colorScheme="purple"
              onClick={() => handleGenreToggle(genre)}
              isDisabled={!formData.genres.includes(genre) && formData.genres.length >= 3}
            >
              {genre}
            </Button>
          ))}
        </Flex>
      </FormControl>

      {/* Cast, Director, Writer */}
      <HStack gap={4} align="start">
        <FormControl>
          <FormLabel color={labelColor} fontWeight="bold">
            Cast
          </FormLabel>
          <Input
            value={formData.cast}
            onChange={(e) => handleInputChange('cast', e.target.value)}
            placeholder="Character names (comma separated)"
            bg={inputBg}
            borderColor={borderColor}
          />
        </FormControl>
        <FormControl>
          <FormLabel color={labelColor} fontWeight="bold">
            Artist
          </FormLabel>
          <Input
            value={formData.director}
            onChange={(e) => handleInputChange('director', e.target.value)}
            placeholder="Artist name(s)"
            bg={inputBg}
            borderColor={borderColor}
          />
        </FormControl>
        <FormControl>
          <FormLabel color={labelColor} fontWeight="bold">
            Writer
          </FormLabel>
          <Input
            value={formData.writer}
            onChange={(e) => handleInputChange('writer', e.target.value)}
            placeholder="Writer name(s)"
            bg={inputBg}
            borderColor={borderColor}
          />
        </FormControl>
      </HStack>

      {/* Content Rating */}
      <FormControl>
        <FormLabel color={labelColor} fontWeight="bold">
          Content Rating
        </FormLabel>
        <RadioGroup
          value={formData.rating}
          onChange={(value) => handleInputChange('rating', value)}
        >
          <HStack gap={4} wrap="wrap">
            {CONTENT_RATINGS.map(rating => (
              <Radio key={rating.value} value={rating.value} colorScheme="purple">
                {rating.label}
              </Radio>
            ))}
          </HStack>
        </RadioGroup>
      </FormControl>
    </VStack>
  );

  const renderStep2 = () => (
    <VStack gap={6} align="stretch">
      {/* Comic Cover */}
      <FormControl>
        <FormLabel color={labelColor} fontWeight="bold">
          Comic Cover
        </FormLabel>
        <Box
          border="2px dashed"
          borderColor={formData.coverFile ? 'purple.300' : borderColor}
          borderRadius="lg"
          p={8}
          textAlign="center"
          bg={formData.coverFile ? 'purple.50' : inputBg}
          _dark={{ bg: formData.coverFile ? 'purple.900' : 'gray.700' }}
          cursor="pointer"
          _hover={{ borderColor: 'purple.400' }}
          transition="all 0.2s"
          onClick={() => document.getElementById('cover-upload')?.click()}
          minH="200px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {formData.coverFile ? (
            <VStack gap={3}>
              <Box position="relative">
                <Image
                  src={formData.coverUrl!}
                  alt="Cover preview"
                  maxH="150px"
                  maxW="200px"
                  objectFit="cover"
                  borderRadius="md"
                />
                <Button
                  position="absolute"
                  top={-2}
                  right={-2}
                  size="sm"
                  colorScheme="red"
                  variant="solid"
                  borderRadius="full"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCoverRemove();
                  }}
                >
                  <IconWrapper icon={FiX} size={12} />
                </Button>
              </Box>
            </VStack>
          ) : (
            <VStack gap={2}>
              <IconWrapper icon={FiPlus} size={32} color={coverIconColor} />
              <Text color={coverTextColor}>Click to upload cover</Text>
            </VStack>
          )}
          <Input
            id="cover-upload"
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleCoverSelect(file);
            }}
            display="none"
          />
        </Box>
      </FormControl>

      {/* Volumes Management */}
      <FormControl>
        <HStack justify="space-between" align="center">
          <FormLabel color={labelColor} fontWeight="bold" mb={0}>
            Volumes * (Add at least one)
          </FormLabel>
          <Button
            size="sm"
            colorScheme="purple"
            leftIcon={<IconWrapper icon={FiPlus} size={16} />}
            onClick={addVolume}
            bg={addCoverButtonBg}
            color="white"
            _hover={{ bg: addCoverButtonHoverBg }}
          >
            Add Cover
          </Button>
        </HStack>
        
        {formData.volumes.length > 0 && (
          <TableContainer>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Cover</Th>
                  <Th>Title</Th>
                  <Th>Pages</Th>
                  <Th>Volume File (PDF)</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {formData.volumes.map((volume, index) => (
                  <Tr key={volume.id}>
                    <Td>
                      {volume.coverUrl ? (
                        <Image
                          src={volume.coverUrl}
                          alt="Volume cover"
                          boxSize="40px"
                          objectFit="cover"
                          borderRadius="md"
                        />
                      ) : (
                        <Box
                          boxSize="40px"
                          bg="gray.100"
                          borderRadius="md"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          cursor="pointer"
                          onClick={() => document.getElementById(`volume-cover-${volume.id}`)?.click()}
                        >
                          <IconWrapper icon={FiPlus} size={16} color="gray.400" />
                        </Box>
                      )}
                      <Input
                        id={`volume-cover-${volume.id}`}
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleVolumeCoverSelect(volume.id, file);
                        }}
                        display="none"
                      />
                    </Td>
                    <Td>
                      <Input
                        value={volume.title}
                        onChange={(e) => updateVolume(volume.id, 'title', e.target.value)}
                        placeholder="Volume title"
                        size="sm"
                        bg={inputBg}
                        borderColor={borderColor}
                      />
                    </Td>
                    <Td>
                      <Input
                        type="number"
                        value={volume.pages || ''}
                        onChange={(e) => updateVolume(volume.id, 'pages', e.target.value ? parseInt(e.target.value) : undefined)}
                        placeholder="Pages (optional)"
                        size="sm"
                        bg={inputBg}
                        borderColor={borderColor}
                        w="100px"
                      />
                    </Td>
                    <Td>
                      <VStack align="start" gap={2}>
                        <HStack>
                          <Button size="xs" colorScheme="purple" variant="outline" onClick={() => document.getElementById(`volume-file-${volume.id}`)?.click()}>
                            {volume.fileName ? 'Change File' : 'Select File'}
                          </Button>
                          {volume.fileName && (
                            <Button size="xs" colorScheme="red" variant="ghost" onClick={() => {
                              updateVolume(volume.id, 'file', null);
                              updateVolume(volume.id, 'fileName', '');
                            }}>
                              Remove
                            </Button>
                          )}
                        </HStack>
                        <Text fontSize="xs" color={labelColor} noOfLines={1} maxW="220px">
                          {volume.fileName || 'PDF required'}
                        </Text>
                        <Input
                          id={`volume-file-${volume.id}`}
                          type="file"
                          accept=".pdf,application/pdf"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              updateVolumeFile(volume.id, file);
                            }
                          }}
                          display="none"
                        />
                      </VStack>
                    </Td>
                    <Td>
                      <Button
                        size="xs"
                        colorScheme="red"
                        variant="outline"
                        onClick={() => removeVolume(volume.id)}
                      >
                        <IconWrapper icon={FiX} size={12} />
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </FormControl>
    </VStack>
  );

  if (!user) {
    return (
      <Center h="50vh">
        <VStack gap={4}>
          <Text fontSize="lg" color="gray.500">
            Please log in to upload comics.
          </Text>
          <Button onClick={() => navigate('/login')} colorScheme="purple">
            Log In
          </Button>
        </VStack>
      </Center>
    );
  }

  return (
    <Box minH="100vh" bg={bgColor}>
      {/* Header */}
      <Box p={6} borderBottom="1px solid" borderColor={borderColor}>
        <HStack gap={4} align="center" maxW="1200px" mx="auto">
          <IconButton
            aria-label="Go back"
            icon={<IconWrapper icon={FiArrowLeft} size={20} />}
            variant="ghost"
            onClick={() => navigate('/upload')}
            size="sm"
            bg={backButtonBg}
            color={backButtonColor}
            _hover={{ bg: backButtonHoverBg }}
            _dark={{ bg: 'purple.800', color: 'white', _hover: { bg: 'purple.700' } }}
          />
          <VStack align="start" gap={1}>
            <Text fontSize="xl" fontWeight="bold" color={textColor}>
              Upload Comic
            </Text>
            <Text color={labelColor}>
              Step {currentStep} of 2
            </Text>
          </VStack>
        </HStack>
      </Box>

      {/* Main Content */}
      <Box p={6} maxW="1000px" mx="auto">
        {currentStep === 1 ? renderStep1() : renderStep2()}

        {/* Progress Bar */}
        {isUploading && (
          <VStack gap={2} mt={6}>
            <Text fontSize="sm" color={labelColor}>
              Uploading... {Math.round(uploadProgress)}%
            </Text>
            <Progress
              value={uploadProgress}
              size="sm"
              colorScheme="purple"
              w="100%"
              borderRadius="full"
            />
          </VStack>
        )}

        {/* Navigation Buttons */}
        <HStack gap={4} mt={8}>
          {currentStep > 1 && (
            <Button
              variant="outline"
              colorScheme="purple"
              onClick={prevStep}
              flex="1"
            >
              Back
            </Button>
          )}
          <Button
            colorScheme="purple"
            onClick={currentStep === 2 ? handleSubmit : nextStep}
            isLoading={isUploading}
            loadingText="Uploading..."
            flex="1"
          >
            {currentStep === 2 ? 'Upload Comic' : 'Continue'}
          </Button>
        </HStack>
      </Box>
    </Box>
  );
};

export default UploadComic;
